import { z } from "zod";
import { businessUnitTypeSchema } from "../auth/features.js";
import { localDateSchema, moneySchema, timestampSchema, uuidSchema } from "../common/primitives.js";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { assetKindSchema } from "../maintenance/api.js";
/**
 * Mevzuat uyum takvimi sözleşmeleri (Prompt 23, ADR 0023 §9–13): platform öneri kataloğu, kiracı belge türleri, belgeler
 * (yenileme / arşiv), denetim ziyaretleri, düzeltici faaliyetler ve takvim. Yasal süreler kiracının tür tanımındadır.
 */
export const COMPLIANCE_SCOPES = [
    "COMPANY",
    "STATION",
    "TANK",
    "DISPENSER",
    "DEVICE",
    "EV_CHARGER",
    "WASH_UNIT",
];
export const complianceScopeSchema = z.enum(COMPLIANCE_SCOPES);
export const COMPLIANCE_SCOPE_LABELS = {
    COMPANY: "Şirket",
    STATION: "İstasyon",
    TANK: "Tank",
    DISPENSER: "Pompa",
    DEVICE: "Cihaz / ekipman",
    EV_CHARGER: "Şarj ünitesi",
    WASH_UNIT: "Yıkama ünitesi",
};
export const COMPLIANCE_DOCUMENT_STATUSES = ["ACTIVE", "SUPERSEDED", "ARCHIVED"];
export const complianceDocumentStatusSchema = z.enum(COMPLIANCE_DOCUMENT_STATUSES);
export const COMPLIANCE_DOCUMENT_STATUS_LABELS = {
    ACTIVE: "Geçerli kayıt",
    SUPERSEDED: "Yenilendi",
    ARCHIVED: "Arşivlendi",
};
export const EXPIRY_STATUSES = ["VALID", "EXPIRING", "EXPIRED", "NO_EXPIRY"];
export const expiryStatusSchema = z.enum(EXPIRY_STATUSES);
export const EXPIRY_STATUS_LABELS = {
    VALID: "Geçerli",
    EXPIRING: "Süresi yaklaşıyor",
    EXPIRED: "Süresi doldu",
    NO_EXPIRY: "Süresiz",
};
export const INSPECTION_AUTHORITIES = [
    "EPDK",
    "MUNICIPALITY",
    "FIRE",
    "METROLOGY",
    "ENVIRONMENT",
    "OHS",
    "TAX",
    "SGK",
    "OTHER",
];
export const inspectionAuthoritySchema = z.enum(INSPECTION_AUTHORITIES);
export const INSPECTION_AUTHORITY_LABELS = {
    EPDK: "EPDK",
    MUNICIPALITY: "Belediye",
    FIRE: "İtfaiye",
    METROLOGY: "Ölçü ve ayar (metroloji)",
    ENVIRONMENT: "Çevre",
    OHS: "İş sağlığı ve güvenliği",
    TAX: "Vergi dairesi",
    SGK: "SGK",
    OTHER: "Diğer kurum",
};
export const INSPECTION_STATUSES = ["PLANNED", "COMPLETED", "CANCELLED"];
export const inspectionStatusSchema = z.enum(INSPECTION_STATUSES);
export const INSPECTION_STATUS_LABELS = {
    PLANNED: "Planlı",
    COMPLETED: "Yapıldı",
    CANCELLED: "İptal",
};
export const INSPECTION_RESULTS = ["NO_FINDINGS", "FINDINGS", "PENALTY"];
export const inspectionResultSchema = z.enum(INSPECTION_RESULTS);
export const INSPECTION_RESULT_LABELS = {
    NO_FINDINGS: "Bulgu yok",
    FINDINGS: "Bulgu var",
    PENALTY: "Ceza",
};
export const CORRECTIVE_ACTION_STATUSES = ["OPEN", "DONE", "CANCELLED"];
export const correctiveActionStatusSchema = z.enum(CORRECTIVE_ACTION_STATUSES);
export const CORRECTIVE_ACTION_STATUS_LABELS = {
    OPEN: "Açık",
    DONE: "Tamamlandı",
    CANCELLED: "İptal",
};
export const ACTION_DUE_STATES = ["OK", "DUE_SOON", "OVERDUE"];
export const actionDueStateSchema = z.enum(ACTION_DUE_STATES);
const versionSchema = z.number().int().positive();
const positiveMoney = moneySchema.refine((value) => /^[1-9]\d*$/.test(value), "Tutar sıfırdan büyük olmalıdır");
const refSchema = z.object({ id: uuidSchema, name: z.string() });
const typeCodeSchema = z
    .string()
    .regex(/^[A-Z][A-Z0-9_]{1,39}$/, "Kod büyük harf, rakam ve alt çizgiden oluşmalı (ör. YANGIN_BELGESI)");
export const reminderDaysSchema = z
    .array(z.number().int().min(1).max(730))
    .max(6, "En fazla 6 hatırlatma günü")
    .refine((days) => new Set(days).size === days.length, "Aynı gün iki kez girilemez")
    .describe("Bitişten kaç gün önce hatırlatılacağı (ör. [60, 30, 7])");
// ── Öneri kataloğu ────────────────────────────────────────────────────────────
export const complianceCatalogItemSchema = z.object({
    code: z.string(),
    name: z.string(),
    description: z.string(),
    appliesTo: complianceScopeSchema,
    businessUnit: businessUnitTypeSchema.nullable(),
    suggestedValidityMonths: z.number().int().nullable(),
    suggestedReminderDays: z.array(z.number().int()),
    needsVerification: z.boolean(),
    /** Kiracı bu öneriyi tür olarak eklediyse türün kimliği. */
    adoptedTypeId: uuidSchema.nullable(),
});
export const complianceCatalogSchema = z.object({ items: z.array(complianceCatalogItemSchema) });
export const adoptCatalogTypesSchema = z.object({
    codes: z.array(typeCodeSchema).min(1, "En az bir öneri seçin").max(50),
});
// ── Belge türleri ─────────────────────────────────────────────────────────────
export const complianceDocumentTypeSchema = z.object({
    id: uuidSchema,
    code: z.string(),
    catalogCode: z.string().nullable(),
    name: z.string(),
    description: z.string().nullable(),
    appliesTo: complianceScopeSchema,
    validityMonths: z.number().int().nullable(),
    reminderDays: z.array(z.number().int()).describe("Büyükten küçüğe"),
    requiresVerification: z.boolean(),
    /** Öneriden gelen tür doğrulanana dek `false` ("doğrulayın" rozeti). */
    verified: z.boolean(),
    verifiedAt: timestampSchema.nullable(),
    verifiedByName: z.string().nullable(),
    isActive: z.boolean(),
    activeDocumentCount: z.number().int().nonnegative(),
    version: z.number().int(),
});
export const complianceDocumentTypeListSchema = z.object({ items: z.array(complianceDocumentTypeSchema) });
export const createComplianceDocumentTypeSchema = z.object({
    code: typeCodeSchema,
    name: z.string().trim().min(1, "Ad zorunludur").max(160),
    description: z.string().trim().max(1000).nullable().default(null),
    appliesTo: complianceScopeSchema,
    validityMonths: z.number().int().min(1).max(240).nullable().default(null),
    reminderDays: reminderDaysSchema,
});
export const updateComplianceDocumentTypeSchema = z
    .object({
    name: z.string().trim().min(1, "Ad zorunludur").max(160).optional(),
    description: z.string().trim().max(1000).nullable().optional(),
    validityMonths: z.number().int().min(1).max(240).nullable().optional(),
    reminderDays: reminderDaysSchema.optional(),
    isActive: z.boolean().optional(),
    version: versionSchema,
})
    .describe("Kod ve kapsam değiştirilemez (belgeler ona bağlı)");
export const verifyComplianceDocumentTypeSchema = z.object({ version: versionSchema });
// ── Belgeler ──────────────────────────────────────────────────────────────────
export const complianceExpirySchema = z.object({
    status: expiryStatusSchema,
    daysLeft: z.number().int().nullable().describe("Eksi = geçti; süresizde null"),
    nextReminderOn: localDateSchema.nullable().describe("Bugünden sonraki ilk hatırlatma günü"),
});
export const complianceDocumentSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    companyName: z.string(),
    stationId: uuidSchema.nullable(),
    stationCode: z.string().nullable(),
    stationName: z.string().nullable(),
    type: z.object({
        id: uuidSchema,
        code: z.string(),
        name: z.string(),
        appliesTo: complianceScopeSchema,
        verified: z.boolean(),
    }),
    asset: z.object({ kind: assetKindSchema, id: uuidSchema, name: z.string() }).nullable(),
    documentNo: z.string().nullable(),
    issuedOn: localDateSchema,
    expiresOn: localDateSchema.nullable(),
    fileId: uuidSchema.nullable(),
    responsibleUser: refSchema.nullable(),
    notes: z.string().nullable(),
    status: complianceDocumentStatusSchema,
    supersededById: uuidSchema.nullable(),
    archivedAt: timestampSchema.nullable(),
    archiveReason: z.string().nullable(),
    expiry: complianceExpirySchema,
    createdAt: timestampSchema,
    version: z.number().int(),
});
export const complianceDocumentDetailSchema = complianceDocumentSchema.extend({
    /** Aynı istasyon / varlık / türde önceki (yenilenmiş) belgeler — en yeni önce. */
    history: z.array(complianceDocumentSchema),
    canManage: z.boolean(),
});
export const complianceDocumentListQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema.optional(),
    stationId: uuidSchema.optional(),
    typeId: uuidSchema.optional(),
    status: z.enum([...COMPLIANCE_DOCUMENT_STATUSES, "ALL"]).default("ACTIVE"),
    expiry: expiryStatusSchema.optional(),
    search: z.string().trim().max(80).optional(),
});
export const complianceDocumentListSchema = pageOf(complianceDocumentSchema);
export const complianceSummaryQuerySchema = z.object({
    companyId: uuidSchema.optional(),
    stationId: uuidSchema.optional(),
});
export const complianceSummarySchema = z.object({
    expired: z.number().int().nonnegative(),
    expiring: z.number().int().nonnegative(),
    valid: z.number().int().nonnegative(),
    noExpiry: z.number().int().nonnegative(),
    unverifiedTypes: z.number().int().nonnegative(),
    openActions: z.number().int().nonnegative(),
    overdueActions: z.number().int().nonnegative(),
    plannedInspections: z.number().int().nonnegative(),
});
const documentBody = {
    documentNo: z.string().trim().min(1).max(80).nullable().default(null),
    issuedOn: localDateSchema,
    expiresOn: localDateSchema.nullable().default(null).describe("Süresiz belgede boş"),
    fileId: uuidSchema.nullable().default(null),
    responsibleUserId: uuidSchema.nullable().default(null),
    notes: z.string().trim().max(2000).nullable().default(null),
};
const expiryAfterIssue = (value, context) => {
    if (value.issuedOn && value.expiresOn && value.expiresOn < value.issuedOn)
        context.addIssue({
            code: "custom",
            path: ["expiresOn"],
            message: "Bitiş tarihi veriliş tarihinden önce olamaz",
        });
};
export const createComplianceDocumentSchema = z
    .object({
    typeId: uuidSchema,
    companyId: uuidSchema
        .nullable()
        .default(null)
        .describe("Şirket düzeyi türde zorunlu; istasyonluda istasyondan"),
    stationId: uuidSchema.nullable().default(null),
    assetKind: assetKindSchema.nullable().default(null),
    assetId: uuidSchema.nullable().default(null),
    ...documentBody,
})
    .superRefine((value, context) => {
    expiryAfterIssue(value, context);
    if (!value.companyId && !value.stationId)
        context.addIssue({ code: "custom", path: ["stationId"], message: "İstasyon ya da şirket seçin" });
    if ((value.assetKind === null) !== (value.assetId === null))
        context.addIssue({
            code: "custom",
            path: ["assetId"],
            message: "Varlık türü ve varlık birlikte seçilmeli",
        });
});
export const renewComplianceDocumentSchema = z
    .object({ ...documentBody, version: versionSchema })
    .superRefine(expiryAfterIssue)
    .describe("Yeni belge açılır, eski belge `SUPERSEDED` olur (geçmiş kalır)");
export const updateComplianceDocumentSchema = z
    .object({
    documentNo: z.string().trim().min(1).max(80).nullable().optional(),
    issuedOn: localDateSchema.optional(),
    expiresOn: localDateSchema.nullable().optional(),
    fileId: uuidSchema.nullable().optional(),
    responsibleUserId: uuidSchema.nullable().optional(),
    notes: z.string().trim().max(2000).nullable().optional(),
    version: versionSchema,
})
    .superRefine(expiryAfterIssue)
    .describe("Kayıt düzeltmesi (denetim izine yazılır); yeni dönem için yenileme kullanılır");
export const archiveComplianceDocumentSchema = z.object({
    reason: z.string().trim().min(3, "En az 3 karakter").max(500),
    version: versionSchema,
});
/** Sorumlu kişi adayları: kapsamda `compliance.read` izni olan etkin üyeler. */
export const responsibleCandidatesQuerySchema = z.object({
    stationId: uuidSchema.optional(),
    companyId: uuidSchema.optional(),
});
export const responsibleCandidatesSchema = z.object({ items: z.array(refSchema) });
// ── Denetim ziyaretleri ve düzeltici faaliyetler ─────────────────────────────
export const correctiveActionSchema = z.object({
    id: uuidSchema,
    inspectionId: uuidSchema,
    stationId: uuidSchema,
    description: z.string(),
    responsibleUser: refSchema.nullable(),
    dueDate: localDateSchema,
    status: correctiveActionStatusSchema,
    dueState: actionDueStateSchema.nullable(),
    completedAt: timestampSchema.nullable(),
    completionNote: z.string().nullable(),
    evidenceFileId: uuidSchema.nullable(),
    createdAt: timestampSchema,
    version: z.number().int(),
});
export const inspectionSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    stationId: uuidSchema,
    stationCode: z.string(),
    stationName: z.string(),
    authority: inspectionAuthoritySchema,
    authorityName: z.string().nullable(),
    visitDate: localDateSchema,
    status: inspectionStatusSchema,
    result: inspectionResultSchema.nullable(),
    penaltyAmount: moneySchema.nullable(),
    reportFileId: uuidSchema.nullable(),
    notes: z.string().nullable(),
    actions: z.object({
        total: z.number().int().nonnegative(),
        open: z.number().int().nonnegative(),
        overdue: z.number().int().nonnegative(),
    }),
    createdAt: timestampSchema,
    version: z.number().int(),
});
export const inspectionDetailSchema = inspectionSchema.extend({
    correctiveActions: z.array(correctiveActionSchema),
    canManage: z.boolean(),
});
export const inspectionListQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema.optional(),
    stationId: uuidSchema.optional(),
    status: inspectionStatusSchema.optional(),
    authority: inspectionAuthoritySchema.optional(),
    from: localDateSchema.optional(),
    to: localDateSchema.optional(),
    openActions: z.enum(["true"]).optional().describe("Yalnızca açık düzeltici faaliyeti olanlar"),
});
export const inspectionListSchema = pageOf(inspectionSchema);
const resultRules = (value, context) => {
    if (value.status === "COMPLETED" && !value.result)
        context.addIssue({ code: "custom", path: ["result"], message: "Yapılmış denetimde sonuç seçin" });
    if (value.status === "PLANNED" && value.result)
        context.addIssue({ code: "custom", path: ["result"], message: "Planlı denetimde sonuç girilmez" });
    if (value.penaltyAmount != null && value.result !== "PENALTY")
        context.addIssue({
            code: "custom",
            path: ["penaltyAmount"],
            message: "Ceza tutarı yalnızca ceza sonucunda girilir",
        });
};
export const createInspectionSchema = z
    .object({
    stationId: uuidSchema,
    authority: inspectionAuthoritySchema,
    authorityName: z.string().trim().min(1).max(160).nullable().default(null),
    visitDate: localDateSchema,
    status: z.enum(["PLANNED", "COMPLETED"]).default("PLANNED"),
    result: inspectionResultSchema.nullable().default(null),
    penaltyAmount: positiveMoney.nullable().default(null),
    reportFileId: uuidSchema.nullable().default(null),
    notes: z.string().trim().max(4000).nullable().default(null),
})
    .superRefine((value, context) => {
    resultRules(value, context);
    if (value.authority === "OTHER" && !value.authorityName)
        context.addIssue({ code: "custom", path: ["authorityName"], message: "Kurum adını yazın" });
});
export const updateInspectionSchema = z.object({
    authorityName: z.string().trim().min(1).max(160).nullable().optional(),
    visitDate: localDateSchema.optional(),
    reportFileId: uuidSchema.nullable().optional(),
    notes: z.string().trim().max(4000).nullable().optional(),
    version: versionSchema,
});
export const completeInspectionSchema = z
    .object({
    result: inspectionResultSchema,
    penaltyAmount: positiveMoney.nullable().default(null),
    reportFileId: uuidSchema.nullable().default(null),
    notes: z.string().trim().max(4000).nullable().default(null),
    version: versionSchema,
})
    .superRefine((value, context) => resultRules({ ...value, status: "COMPLETED" }, context));
export const cancelInspectionSchema = z.object({
    reason: z.string().trim().min(3, "En az 3 karakter").max(500),
    version: versionSchema,
});
export const inspectionParamsSchema = z.object({ id: uuidSchema });
export const correctiveActionParamsSchema = z.object({ id: uuidSchema, actionId: uuidSchema });
export const createCorrectiveActionSchema = z.object({
    description: z.string().trim().min(3, "En az 3 karakter").max(1000),
    responsibleUserId: uuidSchema.nullable().default(null),
    dueDate: localDateSchema,
});
export const updateCorrectiveActionSchema = z.object({
    description: z.string().trim().min(3, "En az 3 karakter").max(1000).optional(),
    responsibleUserId: uuidSchema.nullable().optional(),
    dueDate: localDateSchema.optional(),
    version: versionSchema,
});
export const completeCorrectiveActionSchema = z.object({
    completionNote: z.string().trim().min(3, "En az 3 karakter").max(2000),
    evidenceFileId: uuidSchema.nullable().default(null),
    version: versionSchema,
});
export const cancelCorrectiveActionSchema = z.object({
    reason: z.string().trim().min(3, "En az 3 karakter").max(2000),
    version: versionSchema,
});
// ── Takvim ────────────────────────────────────────────────────────────────────
export const CALENDAR_EVENT_KINDS = [
    "DOCUMENT_EXPIRY",
    "DOCUMENT_REMINDER",
    "INSPECTION",
    "CORRECTIVE_ACTION",
    "WORK_ORDER_DUE",
    "PLAN_DUE",
];
export const calendarEventKindSchema = z.enum(CALENDAR_EVENT_KINDS);
export const CALENDAR_EVENT_KIND_LABELS = {
    DOCUMENT_EXPIRY: "Belge bitişi",
    DOCUMENT_REMINDER: "Belge hatırlatması",
    INSPECTION: "Denetim ziyareti",
    CORRECTIVE_ACTION: "Düzeltici faaliyet",
    WORK_ORDER_DUE: "İş emri son anı",
    PLAN_DUE: "Bakım planı vadesi",
};
export const CALENDAR_MAX_DAYS = 400;
export const complianceCalendarQuerySchema = z
    .object({
    from: localDateSchema,
    to: localDateSchema,
    companyId: uuidSchema.optional(),
    stationId: uuidSchema.optional(),
    kinds: z.string().optional().describe("Virgülle ayrılmış olay türleri (boşsa hepsi)"),
})
    .refine((value) => value.from <= value.to, {
    message: "Başlangıç tarihi bitişten sonra olamaz",
    path: ["from"],
});
export const calendarEventSchema = z.object({
    key: z.string().describe("Olay türü + kaynak kimliği (+ hatırlatma günü)"),
    kind: calendarEventKindSchema,
    date: localDateSchema,
    stationId: uuidSchema.nullable(),
    stationCode: z.string().nullable(),
    title: z.string(),
    detail: z.string().nullable(),
    tone: z.enum(["INFO", "WARNING", "CRITICAL", "DONE"]),
    sourceId: uuidSchema,
    link: z.string().describe("Web yolu"),
});
export const complianceCalendarSchema = z.object({
    from: localDateSchema,
    to: localDateSchema,
    events: z.array(calendarEventSchema),
    /** İzin yüzünden süzülen kaynaklar (ör. bakım okuma izni yoksa iş emirleri). */
    hiddenKinds: z.array(calendarEventKindSchema),
});
//# sourceMappingURL=api.js.map